/**
 * Purpose: Periodically re-syncs `activeCallIds` against server truth while
 * the socket stays connected, so a "ghost" active-call indicator self-heals
 * without requiring a socket reconnect.
 *
 * Background (see call-status-rca.md, root causes #1/#2): the backend's
 * Jitsi room metadata expires via a 90s Redis TTL (ROOM_TTL_SECS) as a
 * backstop for missed `meeting.ended` webhooks (pod eviction, dropped
 * fire-and-forget webhook POST, etc). Nothing currently observes that TTL
 * expiry to push a `meeting-ended` event, so a client that misses the live
 * event has no way to notice a call ended other than reconciling against
 * `get-active-rooms` — which, before this module, only fired once on socket
 * `connect`. A long-lived, never-reconnecting tab therefore had no
 * self-healing path and could show a ghost call for the entire session.
 *
 * This module reuses the exact same `GET_ACTIVE_ROOMS` emit + ack path as
 * the connect-time sync (`getGetActiveRoomsCallback`, which fully replaces
 * `activeCallIds` from the server's authoritative list) — it does not
 * introduce a second/parallel seeding mechanism.
 */

import type { Socket } from "socket.io-client";
import { SocketEventTypes } from "./types";
import { getGetActiveRoomsCallback } from "./callsEventsHandler";

// Refresh well inside the server's 90s room TTL (ROOM_TTL_SECS) so a ghost
// call clears itself within roughly half a TTL window instead of persisting
// for the life of a long-running tab. 45s also keeps this cheap relative to
// the existing 20s heartbeat cadence.
export const ACTIVE_ROOMS_REFRESH_INTERVAL_MS = 45000;

let activeRoomsRefreshInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Starts the periodic `get-active-rooms` refresh for the given socket.
 *
 * Safe to call on every `connect` (including reconnects): it always clears
 * any previously running interval first, so repeated calls never stack
 * multiple intervals.
 */
export const startActiveRoomsRefresh = (socket: Socket): void => {
  stopActiveRoomsRefresh();

  // TODO: like the heartbeat interval (stopHeartbeat, useSocket.ts), this
  // interval is only guaranteed to be cleared via the socket's `disconnect`
  // event / cleanupSocketInstance() — logoutUser() (middleware/auth.ts)
  // never calls stopActiveRoomsRefresh() or disconnects the socket directly.
  // Pre-existing gap, shared with the heartbeat; harmless no-op post-logout
  // (the ack callback bails out via isUserAuthenticated()), but both should
  // be cleaned up together if the logout lifecycle is ever tightened.
  activeRoomsRefreshInterval = setInterval(() => {
    if (socket.connected) {
      socket.emit(SocketEventTypes.GET_ACTIVE_ROOMS, getGetActiveRoomsCallback);
    }
  }, ACTIVE_ROOMS_REFRESH_INTERVAL_MS);
};

/**
 * Stops the periodic refresh, if running. Idempotent.
 */
export const stopActiveRoomsRefresh = (): void => {
  if (activeRoomsRefreshInterval) {
    clearInterval(activeRoomsRefreshInterval);
    activeRoomsRefreshInterval = null;
  }
};
